import {
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Icon,
  Input,
  Link,
  Select,
  Stack,
  Text,
  useNumberInput,
  useToast,
} from "@chakra-ui/react";
import {
  HiPlus,
  IoMdRemove,
  MdOutlinePictureAsPdf,
  MdShoppingCart,
} from "../../customIcons";
import React, { FC, useEffect, useState } from "react";
import {
  calculateDiscountPercentage,
  getProductPrice,
} from "../../utils/shopUtils";

import ADD_TO_CART from "../../types/AddToCard";
import CrossSellsSection from "../CrossSellsSection";
import GridTable from "../../molecules/GridTable";
import { brandsToExclude } from "../../../queries/toExclude";
import { navigate } from "gatsby";
import { useAppState } from "../../context";
import useAuth from "../../useAuth";
import { useMutation } from "@apollo/client";
import { error } from "console";

enum WpStockStatusEnum {
  IN_STOCK = "IN_STOCK",
  OUT_OF_STOCK = "OUT_OF_STOCK",
  ON_BACKORDER = "ON_BACKORDER",
}

type SingleProductDetailsType = {
  title?: any;
  discount?: string;
  price?: string;
  description?: string;
  attributesArr?: any[] | undefined;
  productId: number;
  tierPrice?: any;
  brandName: any;
  stockStatus: WpStockStatusEnum;
  salePrice: string;
  regularPrice: string;
  pdf?: any;
  bundleTitle: string;
  bundleDescription: string;
  bundles: any;
  isOnPromotion: any;
  isVariableProduct?: boolean;
  variations?: any;
  selectedVariation?: any | null;
  onVariationChange?: (event: any) => void;
  defaultPrice?: string;
};

const SingleProductDetails: FC<SingleProductDetailsType> = ({
  title,
  attributesArr,
  productId,
  price: initialPrice,

  tierPrice,
  brandName,
  salePrice: initialSalePrice,
  regularPrice: initialRegularPrice,
  pdf,
  bundleTitle,
  bundleDescription,
  bundles,
  isOnPromotion,
  isVariableProduct,
  variations,
  selectedVariation,
  onVariationChange,
  defaultPrice,
}) => {
  const toast = useToast();
  const { setCart } = useAppState();

  const { loggedIn, isUnlicensedUser, isCustomerDefault, isBankAcc, user } =
    useAuth();

  const isRestricted = brandName.every((item: any) =>
    brandsToExclude.includes(item.name),
  );

  const [selectedVariationData, setSelectedVariationData] = useState<any>(null);
  const [price, setPrice] = useState(initialPrice);
  const [salePrice, setSalePrice] = useState(initialSalePrice);
  const [regularPrice, setRegularPrice] = useState(initialRegularPrice);

  useEffect(() => {
    if (selectedVariation) {
      const variation = variations?.find(
        (item: { databaseId: any }) => item.databaseId === selectedVariation,
      );

      if (variation) {
        setSelectedVariationData(variation);
        setPrice(variation.price);
        setSalePrice(variation.salePrice);
        setRegularPrice(variation.regularPrice);
        setSelectedVariationId(variation.databaseId);
      }
    } else {
      setSelectedVariationData(null);
      setPrice(defaultPrice);
      setSalePrice(initialSalePrice);
      setRegularPrice(initialRegularPrice);
      setSelectedVariationId("");
    }
  }, [selectedVariation, variations, defaultPrice]);

  const formatPrice = (price: string | undefined): string => {
    if (!price) return "";
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""));
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numericPrice);
  };

  const tierPricesData = [
    {
      amount: "1 - 2",
      discount: "-",
      price: tierPrice.tierPrice?.[0]?.price,
    },
    {
      amount: "3 - 10",
      discount: `${calculateDiscountPercentage(
        tierPrice.tierPrice?.[0]?.price,
        tierPrice.tierPrice?.[0]?.price2,
      )}%`,
      price: tierPrice.tierPrice?.[0]?.price2,
    },
    {
      amount: "11 - 20",
      discount: `${calculateDiscountPercentage(
        tierPrice.tierPrice?.[0]?.price,
        tierPrice.tierPrice?.[0]?.price3,
      )}%`,
      price: tierPrice.tierPrice?.[0]?.price3,
    },
    {
      amount: "21+",
      discount: `${calculateDiscountPercentage(
        tierPrice.tierPrice?.[0]?.price,
        tierPrice.tierPrice?.[0]?.price4,
      )}%`,
      price: tierPrice.tierPrice?.[0]?.price4,
    },
  ];

  const [currentTierPrice, setCurrentTierPrice] = useState(
    tierPrice.tierPrice?.[0]?.price,
  );

  const [addToCart, { loading }] = useMutation(ADD_TO_CART, {
    onCompleted: ({ addToCart }) => {
      toast({
        title: (
          <Text>
            Added to&nbsp;
            <Link
              href="https://www.medsupplysolutions.com/cart/"
              fontWeight={700}
              textDecoration="underline"
            >
              cart
            </Link>
          </Text>
        ),
        status: "success",
      });
      setCart(addToCart.cart);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
        user_id: loggedIn ? user?.id : "guest",
        ecommerce: {
          items: [
            {
              item_name: title,
              item_id: productId,
              price: price ? parseFloat(price.replace("$", "")) : undefined,
              quantity: "1",
            },
          ],
        },
      });
    },
    onError: (error) => {
      console.error("Error adding to cart:", error); // Log the error to the console
      toast({
        title: "Error",
        description:
          "The quantity added to your cart is higher than the available stock. Please reduce it to continue.",
        status: "error",
      });
    },
  });

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      defaultValue: 1,
      min: 1,
      max: 999,
      precision: 0,
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  useEffect(() => {
    if (input.value) {
      const newTierPrice = getProductPrice(input.value, tierPrice, price!);
      setCurrentTierPrice(newTierPrice);
    }
  }, [input.value]);

  const handleAddToCart = () => {
    const productToAdd = selectedVariationData?.databaseId || productId;
    // const priceToSend = selectedVariationData?.price || price;

    addToCart({
      variables: {
        input: {
          productId: productToAdd,
          // variationId: selectedVariationData?.databaseId || null,
          quantity: Number(input.value),
        },
      },
      update: (cache, { data: { addToCart } }) => {
        cache.modify({
          fields: {
            cart(existingCart = {}) {
              return {
                ...existingCart,
                contents: {
                  ...existingCart.contents,
                  nodes: [
                    ...(existingCart.contents?.nodes || []),
                    ...addToCart.cart.contents.nodes,
                  ],
                },
              };
            },
          },
        });
      },
    });
  };

  const [selectedVariationId, setSelectedVariationId] = useState(
    selectedVariation || "",
  );

  const isVariationNotSelected =
    !selectedVariationId || selectedVariationId === "Choose";

  const handleVariationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedId = Number(event.target.value);
    const variation = variations.find(
      (item: any) => item.databaseId === selectedId,
    );

    setSelectedVariationData(variation);
    setPrice(variation?.price || defaultPrice);
    setSalePrice(variation?.salePrice || initialSalePrice);
    setRegularPrice(variation?.regularPrice || initialRegularPrice);
    setSelectedVariationId(selectedId);

    if (onVariationChange) {
      onVariationChange(selectedId);
    }
  };

  return (
    <Container zIndex={1} w={{ base: "100vw", md: "100%" }} maxW="800px">
      <Stack gap={0}>
        <Heading
          pt={{ base: 5, xl: 0 }}
          as="h1"
          fontSize={"2xl"}
          fontWeight="700"
        >
          {title}
        </Heading>

        {(!loggedIn || isUnlicensedUser || isCustomerDefault || isBankAcc) &&
        isRestricted ? (
          <Button
            borderRadius="4px"
            backgroundColor="#3B2060"
            color="#fff"
            h={12}
            w={{ base: "343px", md: "464px" }}
            mt={{ base: 6, md: 12 }}
            _hover={{ background: "#6A3AAB" }}
            fontSize="md"
            fontWeight="600"
            onClick={() => {
              navigate("/contact/");
            }}
          >
            Contact us
          </Button>
        ) : (
          <>
            {isVariableProduct && variations.length > 0 && (
              <Box pt="16px" width="100%" maxW="auto">
                <Select
                  focusBorderColor="#BB5D82"
                  onChange={handleVariationChange}
                  value={selectedVariationId || defaultPrice}
                >
                  <option>Choose</option>
                  {variations.map((item: any) => (
                    <option key={item.databaseId} value={item.databaseId}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
            )}

            <>
              <Stack
                display="flex"
                flexDir={{ base: "column", md: "row" }}
                alignItems="flex-start"
                pt={6}
                pb={4}
                gap={0}
              >
                <Box display={"flex"} flexDir={"row"} alignItems="center">
                  {salePrice ? (
                    <HStack pb="8px" px={4} h={10}>
                      <Text fontSize="lg" fontWeight="600" color="#39A26A">
                        {formatPrice(salePrice)}
                      </Text>
                      <Text
                        fontSize="14px"
                        color="#4A5568"
                        textDecor="line-through"
                      >
                        {formatPrice(regularPrice)}
                      </Text>
                    </HStack>
                  ) : (
                    <Text fontSize="20px" mr={2} fontWeight="600">
                      {isVariableProduct
                        ? price
                        : currentTierPrice
                        ? `${formatPrice(currentTierPrice)}`
                        : price}
                    </Text>
                  )}
                  {(!isVariationNotSelected || !isVariableProduct) && (
                    <HStack
                      gap="0"
                      width="108px"
                      border="1px solid #CBD5E0"
                      borderRadius="6px"
                      h="48px"
                      p="8px"
                      mr="24px"
                    >
                      <Icon
                        height="14px"
                        width="14px"
                        as={IoMdRemove}
                        color={input.value === "1" ? "#728197" : "#8353C4"}
                        {...dec}
                        cursor={input.value === "1" ? "not-allowed" : ""}
                      />
                      <Input
                        maxLength={3}
                        focusBorderColor="transparent"
                        border="none"
                        {...input}
                        textAlign="center"
                      />
                      <Icon
                        as={HiPlus}
                        height="14px"
                        width="14px"
                        {...inc}
                        color={input.value === "999" ? "#728197" : "#8353C4"}
                        cursor={input.value === "999" ? "not-allowed" : ""}
                      />
                    </HStack>
                  )}
                </Box>
                {(!isVariationNotSelected || !isVariableProduct) && (
                  <Button
                    borderRadius="4px"
                    backgroundColor="#3B2060"
                    color="#fff"
                    h="48px"
                    w={{ base: "100%", md: "464px" }}
                    mt={{ base: 6, md: 0 }}
                    _hover={{ background: "#6A3AAB" }}
                    fontSize="md"
                    fontWeight="600"
                    leftIcon={<MdShoppingCart fill="#fff" w="18px" h="18px" />}
                    isLoading={loading}
                    loadingText="Adding to cart"
                    onClick={() => {
                      handleAddToCart();
                    }}
                  >
                    Add to cart
                  </Button>
                )}
              </Stack>

              <>
                <Stack paddingTop={{ base: 6, xl: 0 }}>
                  {bundles &&
                    bundleTitle &&
                    !selectedVariation?.name
                      ?.toLowerCase()
                      ?.includes("short dated") && (
                      <CrossSellsSection
                        title={bundleTitle}
                        description={bundleDescription}
                        bundles={bundles}
                        productPrice={price}
                        productId={productId}
                      />
                    )}
                </Stack>
                {!isOnPromotion && (
                  <Box
                    w="100%"
                    display={
                      !isVariableProduct ||
                      (selectedVariation?.name &&
                        !selectedVariation.name
                          .toLowerCase()
                          .includes("short dated"))
                        ? "flex"
                        : "none"
                    }
                  >
                    <GridTable data={tierPricesData} />
                  </Box>
                )}
              </>
            </>
          </>
        )}

        <Box pt="16px">
          {attributesArr?.map((item: any, index: any) => {
            if (
              item.name !== "ACTIVE INGREDIENT" &&
              item.name !== "Condition"
            ) {
              return (
                <HStack key={index} alignItems="flex-start">
                  <Box>
                    <Text
                      pr={{ base: 2, xl: "20px" }}
                      w="160px"
                      fontSize="sm"
                      fontWeight="700"
                    >
                      {item.name}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="sm"
                      color="#4A5568"
                      textTransform={"capitalize"}
                      wordBreak="break-all"
                    >
                      {item.options}
                    </Text>
                  </Box>
                </HStack>
              );
            } else {
              return <></>;
            }
          })}
        </Box>
      </Stack>

      {pdf && (
        <Box pt={4} pb={{ base: 8, md: 0 }}>
          <Link
            href={pdf}
            target="_blank"
            rel="noopener"
            _hover={{ textDecoration: "none" }}
          >
            <Button
              color="#BB5D82"
              fontWeight={600}
              lineHeight="140%"
              bg="transparent"
              w={{ base: "100%", md: "auto" }}
              h="40px"
              border="1px solid #CB809E"
              borderRadius="4px"
              _hover={{ bg: "#DBA4B9", color: "#fff" }}
              rightIcon={<MdOutlinePictureAsPdf />}
            >
              See product leaflet
            </Button>
          </Link>
        </Box>
      )}
    </Container>
  );
};

export default SingleProductDetails;
